'use client'

import { useQuery } from '@tanstack/react-query'

const TWEET_PROXY_BASE = 'https://tweet-proxy-production.up.railway.app'

export const TWEET_CATEGORIES = [
	'Crypto',
	'Politics',
	'Sports',
	'Financials',
	'Entertainment',
	'World',
	'Tech',
] as const

export type TweetCategory = (typeof TWEET_CATEGORIES)[number]

export interface TweetEngagement {
	impressions: number
	likes: number
	retweets: number
	replies: number
}

export interface TweetUserData {
	twitterId: number
	username: string
	displayName: string
	url: string
	profileImageUrl: string
	profileBannerUrl?: string
	followersCount: number
	followingCount: number
	statusesCount: number
	verified: boolean
	blue: boolean
	blueType?: string
	location?: string
	rawDescription?: string
}

export interface ActivityTweet {
	tweetId: string
	tweetText: string
	authorId: string
	tweetCreatedAt: string
	engagement: TweetEngagement
	userData: TweetUserData
	// biome-ignore lint/style/useNamingConvention: API field name
	_category: TweetCategory
}

export interface TweetFeedResponse {
	count: number
	total: number
	fetched_at: Record<string, string>
	tweets: ActivityTweet[]
}

function deduplicateTweets(tweets: ActivityTweet[]): ActivityTweet[] {
	const seen = new Set<string>()
	return tweets.filter(tweet => {
		if (seen.has(tweet.tweetId)) {
			return false
		}
		seen.add(tweet.tweetId)
		return true
	})
}

async function fetchTweetFeed(): Promise<TweetFeedResponse> {
	const res = await fetch(`${TWEET_PROXY_BASE}/feed`)
	if (!res.ok) {
		throw new Error('Failed to fetch tweet feed')
	}
	// Convert large numeric IDs to strings BEFORE JSON parsing to preserve precision
	const text = await res.text()
	const safeText = text
		.replace(/"tweetId":\s*(\d+)/g, '"tweetId":"$1"')
		.replace(/"authorId":\s*(\d+)/g, '"authorId":"$1"')
		.replace(/"twitterId":\s*(\d+)/g, '"twitterId":"$1"')
	const data: TweetFeedResponse = JSON.parse(safeText)
	data.tweets = deduplicateTweets(data.tweets)
	data.count = data.tweets.length
	return data
}

async function fetchCategoryTweets(category: TweetCategory): Promise<TweetFeedResponse> {
	const res = await fetch(`${TWEET_PROXY_BASE}/category/${category}`)
	if (!res.ok) {
		throw new Error(`Failed to fetch ${category} tweets`)
	}
	// Convert large numeric IDs to strings BEFORE JSON parsing to preserve precision
	const text = await res.text()
	const safeText = text
		.replace(/"tweetId":\s*(\d+)/g, '"tweetId":"$1"')
		.replace(/"authorId":\s*(\d+)/g, '"authorId":"$1"')
		.replace(/"twitterId":\s*(\d+)/g, '"twitterId":"$1"')
	const data: TweetFeedResponse = JSON.parse(safeText)
	data.tweets = deduplicateTweets(data.tweets)
	data.count = data.tweets.length
	return data
}

export function useActivityTweets(category?: TweetCategory | 'all') {
	return useQuery({
		queryKey: ['activity-tweets', category || 'all'],
		queryFn: () => {
			if (!category || category === 'all') {
				return fetchTweetFeed()
			}
			return fetchCategoryTweets(category)
		},
		staleTime: 5 * 60 * 1000, // 5 minutes
		gcTime: 10 * 60 * 1000, // 10 minutes
		refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
	})
}

// Map ActivityCategory to TweetCategory
export function mapActivityCategoryToTweetCategory(
	activityCategory: string
): TweetCategory | 'all' {
	const mapping: Record<string, TweetCategory | 'all'> = {
		all: 'all',
		crypto: 'Crypto',
		politics: 'Politics',
		sports: 'Sports',
		earnings: 'Financials',
		culture: 'Entertainment',
		world: 'World',
		tech: 'Tech',
	}
	return mapping[activityCategory] || 'all'
}
